import { useState } from "react";
import { useNavigate } from "react-router";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useAuth } from "../contexts/AuthContext";
import { Global } from "../constants/global";
import { NotificationCenter } from "./NotificationCenter";
import { AppRoutes } from "../constants/appRoutes";

export const TopBar = ({ handleDrawerToggle, drawerWidth }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate(AppRoutes.LOGIN);
  };

  const handleProfile = () => {
    handleClose();
    navigate(AppRoutes.PROFILE);
  };

  const envLabel = process.env.VITE_ENV.toUpperCase();
  const showEnvBadge = process.env.VITE_ENV !== Global.PRD;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        transition: (theme) =>
          theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: "none" }, color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>

          {showEnvBadge && (
            <Chip
              label={envLabel}
              size="small"
              sx={{
                bgcolor:
                  envLabel === Global.QAT.toUpperCase()
                    ? "rgba(255, 171, 0, 0.15)"
                    : "rgba(124, 77, 255, 0.15)",
                color:
                  envLabel === Global.QAT.toUpperCase()
                    ? "warning.main"
                    : "secondary.main",
                fontWeight: 700,
                fontSize: "0.7rem",
                display: { xs: "none", sm: "flex" },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NotificationCenter />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, height: 24, my: "auto" }}
          />
          <Box
            sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {user?.username || user?.email || "Admin"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.role ||
                (user?.roles || [])
                  .map((r) => (typeof r === "string" ? r : r.name))
                  .join(", ") ||
                "No role"}
            </Typography>
          </Box>
          <IconButton onClick={handleMenu} size="small">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontSize: "0.9rem",
                fontWeight: 700,
              }}
            >
              {(user?.username || user?.email || "A")[0].toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={handleProfile}>
              <PersonIcon sx={{ mr: 1, fontSize: 20 }} /> Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
